import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './StripePaymentModal.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripePaymentForm = ({ onSuccess, onClose, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // Create payment intent on backend
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';
      
      const intentResponse = await fetch(API_URL + '/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!intentResponse.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret } = await intentResponse.json();

      // Confirm payment with Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: email }
        }
      });

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      if (result.paymentIntent.status === 'succeeded') {
        // Payment successful - confirm subscription on backend
        try {
          const confirmResponse = await fetch(API_URL + '/api/confirm-payment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              paymentIntentId: result.paymentIntent.id
            })
          });

          if (confirmResponse.ok) {
            console.log('✓ Payment confirmed on backend');
          } else {
            console.error('Payment confirmation failed');
          }
        } catch (err) {
          console.error('Error confirming payment:', err);
        }

        // Update UI
        localStorage.setItem('userIsPremium', 'true');
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="stripe-modal-overlay">
      <div className="stripe-modal">
        <div className="stripe-modal-header">
          <h2>Subscribe to Daily Briefing</h2>
          <button 
            className="stripe-modal-close" 
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="stripe-modal-content">
          <div className="stripe-price-display">
            <p className="price">$199<span>/month</span></p>
            <p className="description">Daily geopolitical intelligence briefings</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="stripe-form-group">
              <label>Card Details</label>
              <div className="stripe-card-element">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#e8e9ed',
                        '::placeholder': {
                          color: '#8a8b8f'
                        }
                      },
                      invalid: {
                        color: '#ff6b6b'
                      }
                    }
                  }}
                  onChange={(e) => {
                    setCardComplete(e.complete);
                    if (e.error) {
                      setError(e.error.message);
                    } else {
                      setError('');
                    }
                  }}
                />
              </div>
            </div>

            {error && (
              <div className="stripe-error">
                <p>{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="stripe-submit-btn"
              disabled={!cardComplete || loading}
            >
              {loading ? 'Processing...' : 'Subscribe Now'}
            </button>

            <p className="stripe-disclaimer">
              Your card will be charged $199/month. You can cancel anytime from your account settings.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function StripePaymentModal({ onSuccess, onClose, email }) {
  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm onSuccess={onSuccess} onClose={onClose} email={email} />
    </Elements>
  );
}